/**
 * What the file holds and the case will not: every source column that reaches no entity,
 * property or relationship, in view without opening anything. A column with data and no
 * destination is the one marked, because nothing else tells the analyst it is going nowhere.
 * Pure: the preview's list in.
 */

import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type { UnmappedColumn } from '@/services/api/structuredIngest'

export function UnmappedColumns({ columns, total }: { columns: UnmappedColumn[]; total: number }) {
  const { t } = useTranslation()
  if (columns.length === 0) return null
  return (
    <section className="flex flex-col gap-2">
      <div>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <h3 className="text-base font-semibold">{t('tableMapping.unmapped.title')}</h3>
          <span className="text-sm text-muted-foreground">
            {t('tableMapping.unmapped.ofTotal', { shown: columns.length, total })}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('tableMapping.unmapped.detail')}</p>
      </div>
      <ul className="flex flex-col divide-y rounded-md border">
        {columns.map(column => {
          const reason = t(`tableMapping.unmapped.reason.${column.reason}`)
          return (
            <li
              key={column.name}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1 px-3 py-2 text-sm md:grid-cols-[minmax(0,1fr)_7rem_13rem]"
            >
              <span className="min-w-0 break-all font-mono text-xs">{column.name}</span>
              <span className="text-right text-xs tabular-nums text-muted-foreground">
                {column.filled_rows > 0 &&
                  t('tableMapping.count.rows', { count: column.filled_rows })}
              </span>
              <span className="text-xs text-muted-foreground max-md:col-span-2">
                {column.reason === 'unused' ? <Badge variant="warning">{reason}</Badge> : reason}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
